import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  DocumentTextIcon,
  UserIcon,
  TruckIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ClockIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'

const ContractDetailModal = ({ contract, onClose, onEdit, onStatusChange }) => {
  const [contractDetails, setContractDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [isExportingDocx, setIsExportingDocx] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [loadingInvoices, setLoadingInvoices] = useState(false)
  const [editingDueDate, setEditingDueDate] = useState(null)
  const [newDueDate, setNewDueDate] = useState('')

  useEffect(() => {
    if (contract?.id) {
      loadContractDetails(contract.id)
    }
  }, [contract])

  useEffect(() => {
    if (contractDetails) {
      setSelectedStatus(contractDetails.status)
    }
  }, [contractDetails])
  const loadContractDetails = async (contractId) => {
    try {
      setLoading(true)

      const response = await api.get(`/api/contracts/${contractId}`)
      const result = response.data
      setContractDetails(result.contract)

      // Carregar faturas do contrato
      setLoadingInvoices(true)
      try {
        const invoicesResponse = await api.get(`/api/invoices?contractId=${contractId}`)
        setInvoices(invoicesResponse.data.data || invoicesResponse.data || [])
      } catch (invError) {
        console.error('Erro ao carregar faturas:', invError)
      } finally {
        setLoadingInvoices(false)
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes do contrato:', error)
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateDueDate = async (invoiceId) => {
    if (!newDueDate) return
    try {
      await api.put(`/api/invoices/${invoiceId}/due-date`, { due_date: newDueDate })
      setInvoices(prev => prev.map(inv =>
        inv.id === invoiceId ? { ...inv, due_date: newDueDate } : inv
      ))
      setEditingDueDate(null)
      setNewDueDate('')
      toast.success('Data de vencimento atualizada')
    } catch (error) {
      console.error('Erro ao atualizar vencimento:', error)
      toast.error(error.response?.data?.error || 'Erro ao atualizar vencimento')
    }
  }

  const getInvoiceStatusBadge = (status) => {
    const config = {
      PENDING: { label: 'Pendente', class: 'bg-yellow-100 text-yellow-800' },
      PAID: { label: 'Paga', class: 'bg-green-100 text-green-800' },
      OVERDUE: { label: 'Vencida', class: 'bg-red-100 text-red-800' },
      CANCELLED: { label: 'Cancelada', class: 'bg-gray-100 text-gray-800' }
    }
    const c = config[status] || { label: status, class: 'bg-gray-100 text-gray-800' }
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.class}`}>{c.label}</span>
  }

  if (!contract) return null

  const getStatusBadge = (status) => {
    const badges = {
      negotiating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      sent_to_clicksign: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      awaiting_term: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      awaiting_delivery_term: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      awaiting_delivery_inspection: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      awaiting_return_term: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
      awaiting_return_inspection: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
      finished: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      completed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    }

    const labels = {
      negotiating: 'Em Negociação',
      approved: 'Aprovado',
      sent_to_clicksign: 'Enviado p/ Assinatura',
      awaiting_term: 'Aguard. Termo',
      awaiting_delivery_term: 'Aguard. Termo Entrega',
      awaiting_delivery_inspection: 'Aguard. Vistoria Entrega',
      active: 'Ativo',
      awaiting_return_term: 'Aguard. Termo Devolução',
      awaiting_return_inspection: 'Aguard. Vistoria Devolução',
      finished: 'Concluído',
      completed: 'Concluído',
      cancelled: 'Cancelado'
    }
    
    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const calculateDaysRemaining = (endDate) => {
    if (!endDate) return 0
    const today = new Date()
    const end = new Date(endDate)
    const diffTime = end.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  const calculateTotalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = end.getTime() - start.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  const calculateProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    const today = new Date()
    
    if (today < start) return 0
    if (today > end) return 100
    
    const totalTime = end.getTime() - start.getTime()
    const elapsedTime = today.getTime() - start.getTime()
    return Math.round((elapsedTime / totalTime) * 100)
  }

  const details = contractDetails || contract
  const daysRemaining = calculateDaysRemaining(details.end_date)
  const totalDays = calculateTotalDays(details.start_date, details.end_date)
  const progress = calculateProgress(details.start_date, details.end_date)
  const pendingAmount = details.total_amount || 0 // Por enquanto, assumindo valor total como pendente
  const handleStatusChange = async (newStatus) => {
  if (newStatus === details.status) return

  try {
    setIsUpdatingStatus(true)

    const response = await api.put(`/api/contracts/${details.id}`, { status: newStatus })

    if (contractDetails) {
      setContractDetails({ ...contractDetails, status: newStatus })
    }

    // Notificar componente pai sobre a mudança de status
    if (onStatusChange) {
      onStatusChange(details.id, newStatus)
    }

    toast.success(`Status atualizado para "${newStatus}"`)
  } catch (error) {
    console.error('Erro ao atualizar status:', error)
    setSelectedStatus(details.status)
    const message = error.response?.data?.error || 'Erro ao atualizar status'
    toast.error(message)
  } finally {
    setIsUpdatingStatus(false)
  }
  }

  // Função para exportar contrato como DOCX editável
  const handleExportDocx = async () => {
    try {
      setIsExportingDocx(true)
      toast.loading('Gerando documento DOCX...', { id: 'docx-export' })

      const response = await api.post(`/api/contracts/${details.id}/generate-docx`)

      if (response.data.docxBase64) {
        // Converter base64 para blob
        const byteCharacters = atob(response.data.docxBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: response.data.mimeType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })

        // Criar link de download
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = response.data.fileName || `Contrato_${details.id}.docx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        toast.success('Documento DOCX exportado com sucesso!', { id: 'docx-export' })
      } else {
        throw new Error('Resposta inválida do servidor')
      }
    } catch (error) {
      console.error('Erro ao exportar DOCX:', error)
      const message = error.response?.data?.error || 'Erro ao exportar documento'
      toast.error(message, { id: 'docx-export' })
    } finally {
      setIsExportingDocx(false)
    }
  }

  if (loading && !contractDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-center">
            <div className="spinner mr-3"></div>
            <p className="text-gray-600 dark:text-gray-400">Carregando detalhes do contrato...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <DocumentTextIcon className="w-8 h-8 text-green-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Contrato #{details.id}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {details.client_name} - {details.vehicle_model}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <select
    value={selectedStatus}
    onChange={(e) => {
      setSelectedStatus(e.target.value)
      handleStatusChange(e.target.value)
    }}
    disabled={isUpdatingStatus}
    className="px-3 py-1 rounded-full text-sm font-medium border-2 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <option value="negotiating">Em Negociação</option>
    <option value="approved">Aprovado</option>
    <option value="sent_to_clicksign">Enviado p/ Assinatura</option>
    <option value="awaiting_delivery_term">Aguard. Termo Entrega</option>
    <option value="awaiting_delivery_inspection">Aguard. Vistoria Entrega</option>
    <option value="active">Ativo</option>
    <option value="awaiting_return_term">Aguard. Termo Devolução</option>
    <option value="awaiting_return_inspection">Aguard. Vistoria Devolução</option>
    <option value="completed">Concluído</option>
    <option value="cancelled">Cancelado</option>
            </select>
            <button onClick={onClose}>
             <XMarkIcon className="w-6 h-6" />
           </button>
        </div>
      </div>

        <div className="flex flex-col lg:flex-row max-h-[calc(95vh-80px)] overflow-hidden">
          {/* Informações Principais */}
          <div className="lg:w-1/2 p-6 space-y-6 overflow-y-auto">
            {/* Dados do Cliente */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <UserIcon className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  Cliente
                </h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Nome:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {details.client_name}
                  </span>
                </div>
                {details.client_document && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Documento:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {details.client_document}
                    </span>
                  </div>
                )}
                {details.client_email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Email:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {details.client_email}
                    </span>
                  </div>
                )}
                {details.client_phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Telefone:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {details.client_phone}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Dados do Veículo */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <TruckIcon className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                  Veículo
                </h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Modelo:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {details.vehicle_brand} {details.vehicle_model}
                  </span>
                </div>
                {details.license_plate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Placa:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {details.license_plate}
                    </span>
                  </div>
                )}
                {details.vehicle_year && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Ano:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {details.vehicle_year}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Período do Contrato */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <CalendarDaysIcon className="w-5 h-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                  Período
                </h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Início:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(details.start_date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Fim:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(details.end_date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total de Dias:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {totalDays} dias
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Dias Restantes:</span>
                  <span className={`font-medium ${
                    daysRemaining <= 7 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {daysRemaining} dias
                  </span>
                </div>
              </div>

              {/* Barra de Progresso */}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <span>Progresso do Contrato</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Observações */}
            {details.notes && (
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Observações
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {details.notes}
                </p>
              </div>
            )}
          </div>

          {/* Resumo Financeiro */}
          <div className="lg:w-1/2 bg-gray-50 dark:bg-gray-700/30 p-6 space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <CurrencyDollarIcon className="w-6 h-6 mr-2" />
              Resumo Financeiro
            </h3>

            {/* Valores */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Valor Mensal:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(details.monthly_rate)}
                  </span>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Valor Total:</span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(details.total_amount)}
                  </span>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Pagamentos Pendentes:</span>
                  <span className="text-xl font-bold text-orange-600">
                    {formatCurrency(pendingAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Informações do Sistema */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Informações do Sistema
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Data de Criação:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(details.created_at)}
                  </span>
                </div>
                {details.updated_at && details.updated_at !== details.created_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Última Atualização:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate(details.updated_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Faturas do Contrato - Tabela completa */}
            {details.status === 'active' && (
              <div className="border-t pt-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  Faturas do Contrato ({invoices.length})
                </h4>
                {loadingInvoices ? (
                  <p className="text-gray-500 text-sm">Carregando faturas...</p>
                ) : invoices.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nenhuma fatura gerada</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700 text-left">
                          <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Mês</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Competência</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Vencimento</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Valor</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {invoices
                          .sort((a, b) => new Date(a.period_start) - new Date(b.period_start))
                          .map((inv, idx) => (
                          <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                              {idx + 1}/{invoices.length}
                            </td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                              {inv.period_start ? new Date(inv.period_start).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }) : '—'}
                            </td>
                            <td className="px-3 py-2">
                              {editingDueDate === inv.id ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="date"
                                    value={newDueDate}
                                    onChange={(e) => setNewDueDate(e.target.value)}
                                    className="px-2 py-1 text-xs border border-blue-400 rounded bg-white dark:bg-gray-700 dark:text-white w-36"
                                  />
                                  <button
                                    onClick={() => handleUpdateDueDate(inv.id)}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                  >
                                    OK
                                  </button>
                                  <button
                                    onClick={() => { setEditingDueDate(null); setNewDueDate('') }}
                                    className="px-1 py-1 text-gray-400 hover:text-gray-600 text-xs"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-900 dark:text-white">{formatDate(inv.due_date)}</span>
                                  {!['PAID', 'CANCELLED'].includes(inv.status) && (
                                    <button
                                      onClick={() => {
                                        setEditingDueDate(inv.id)
                                        setNewDueDate(inv.due_date ? new Date(inv.due_date).toISOString().split('T')[0] : '')
                                      }}
                                      className="text-blue-500 hover:text-blue-700 ml-1"
                                      title="Alterar vencimento"
                                    >
                                      <PencilIcon className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                              {formatCurrency(Number(inv.total_value))}
                            </td>
                            <td className="px-3 py-2">
                              {getInvoiceStatusBadge(inv.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Botões de Ação */}
            <div className="border-t pt-6">
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => onEdit && onEdit(details)}
                  className="flex items-center justify-center w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PencilIcon className="w-5 h-5 mr-2" />
                  Editar Contrato
                </button>

                <button
                  onClick={handleExportDocx}
                  disabled={isExportingDocx}
                  className="flex items-center justify-center w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                  {isExportingDocx ? 'Gerando DOCX...' : 'Exportar DOCX Editável'}
                </button>
                
                {details.status === 'active' && (
                  <button
                    onClick={() => handleStatusChange('finished')}
                    disabled={isUpdatingStatus}
                    className="flex items-center justify-center w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    {isUpdatingStatus ? 'Finalizando...' : 'Finalizar Contrato'}
                  </button>
                )}

                {['negotiating', 'approved', 'active'].includes(details.status) && (
                  <button
                    onClick={() => handleStatusChange('cancelled')}
                    disabled={isUpdatingStatus}
                    className="flex items-center justify-center w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircleIcon className="w-5 h-5 mr-2" />
                    {isUpdatingStatus ? 'Cancelando...' : 'Cancelar Contrato'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContractDetailModal